import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import ClientForm from './ClientForm';
import ProjectCompact from 'components/Project/Compact/ProjectCompact';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { CreateClientCommand, UpdateClientCommand } from 'axiosApi/models';

import { SCOPES, ROLES } from 'common/permissions';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import { ButtonWithOptions } from 'components/common/ButtonWithOptions';
import RequiredField from 'components/common/RequiredField';
import intl from 'components/i18n/ReactIntlWrapper';
import { handleAPIError } from 'common/errorHandler';

import { ENTITY_TYPE } from 'common/constants';
import BillingTable from 'components/Billings/BillingTable';
import { PermissionsGate } from 'components/authorization/PermissionsGate';
import AddressCompact from 'components/Address/AddressCompact';
import ContactInfoCompact from 'components/ContactInfo/Compact/ContactInfoCompact';
import Spinner from 'components/Spinner';
import { Box, Button, Card, MenuItem, Stack, Typography } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';

const ClientDetail = () => {
  const DefaultClientDetailInput = {
    name: '',
    tradeName: '',
    currentPage: 1,
  };

  const [input, setInput] = useState(DefaultClientDetailInput);

  const api: Api = useApi();

  const navigate = useNavigate();

  const toast = useToastMessageQueue();

  const { id } = useParams();
  const idNumber = parseInt(id);

  const [addressesId, setAddressesId] = useState<number[]>([]);

  const [contactInfosId, setContactInfosId] = useState<number[]>([]);

  const [loading, setLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState({});

  const [disabled, setDisabled] = useState(false);

  const [disabledNew, setDisabledNew] = useState(false);

  const [countries, setCountries] = useState(null);

  useEffect(() => {
    api.addressApi
      .apiVversionAddressCountryAllGet('1', {})
      .then((response) => {
        setCountries(response.data.data);
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
      });
  }, []);

  const fetchClientDetail = async (idNumber: number) => {
    const response = await api.clientsApi
      .apiVversionClientIdGet(idNumber, '1', {})
      .then((response) => {
        if (response.data.data) {
          setInput({
            ...input,
            name: response.data?.data?.client?.name,
            tradeName: response.data?.data?.client?.tradeName,
          });
          setAddressesId(response.data?.data?.addressIds);
          setContactInfosId(response.data?.data?.contactInfoIds);
        }
      })
      .catch((error) => {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      });
  };

  useEffect(() => {
    if (idNumber) {
      fetchClientDetail(idNumber);
      setDisabled(true);
    }
    setDisabledNew(true);
  }, [idNumber]);

  const handleInputChange = function (e) {
    setInput({
      ...input,
      [e.target.name]: e.target.value,
    });
    setErrors(
      validate({
        ...input,
        [e.target.name]: e.target.value,
      })
    );
  };

  const validate = function (input) {
    setDisabledNew(false);
    let errors: any = {};
    if (!input.name) {
      errors.name = intl.get('validate.errors.isRequired');
    }
    if (!input.tradeName) {
      errors.tradeName = intl.get('validate.errors.isRequired');
    }
    return errors;
  };

  const validateForm = () => {
    const errors = validate(input);
    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const updateExistingClient = async (shouldStayInEditMode: boolean) => {
    const cmd: UpdateClientCommand = {
      name: input.name,
      tradeName: input.tradeName,
      addressesId: addressesId,
      contactInfosId: contactInfosId,
      id: idNumber,
    };

    await api.clientsApi.apiVversionClientPut('1', cmd, {});

    toast.success({
      message: intl.get('clientDetail.toast.success.handleSubmit'),
    });

    if (shouldStayInEditMode) {
      setDisabled(true);
    } else {
      navigate('/clients');
    }
  };

  const createNewClient = async (shouldRedirectToEdit: boolean) => {
    const cmd: CreateClientCommand = {
      name: input.name,
      tradeName: input.tradeName,
    };

    const response = await api.clientsApi.apiVversionClientPost('1', cmd, {});

    toast.success({
      message: intl.get('clientDetail.toast.success.handleSubmit.add'),
    });

    if (shouldRedirectToEdit && response?.data?.data?.id) {
      navigate(`/client/detail/${response.data.data.id}`);
    } else {
      navigate('/clients');
    }
  };

  const handleSubmit = async (action: 'save' | 'saveAndClose') => {
    setLoading(true);

    try {
      if (!validateForm()) {
        return;
      }

      if (idNumber) {
        const shouldStayInEditMode = action === 'save';
        await updateExistingClient(shouldStayInEditMode);
      } else {
        const shouldRedirectToEdit = action === 'save';
        await createNewClient(shouldRedirectToEdit);
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  const handleBackClick = (e) => {
    navigate('/clients');
  };

  const handleSaveClick = () => {
    handleSubmit('save');
  };

  const handleSaveCloseClick = () => {
    handleSubmit('saveAndClose');
  };

  const handleEditMode = (e) => {
    e.preventDefault();
    if (idNumber) {
      fetchClientDetail(idNumber);
    }
    setErrors({});
    setDisabled(!disabled);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
        {idNumber ? intl.get('clientDetail.header') : intl.get('newClient.header')}
      </Typography>
      <Box>
        <ClientForm
          errors={errors}
          input={input}
          handleInputChange={handleInputChange}
          disabled={disabled}
        />
        <RequiredField />
        {!!idNumber && (
          <Stack gap={3} my={4}>
            <Card sx={{ p: 2 }}>
              <AddressCompact
                addressesId={addressesId}
                setAddressesId={setAddressesId}
                disabled={disabled}
                editScopes={[SCOPES['clients.edit']]}
                deleteScopes={[SCOPES['clients.edit']]}
                countries={countries}
              />
            </Card>
            <Card sx={{ p: 2 }}>
              <ContactInfoCompact
                contactInfosId={contactInfosId}
                setContactInfosId={setContactInfosId}
                disabled={disabled}
                editScopes={[SCOPES['clients.edit']]}
                deleteScopes={[SCOPES['clients.edit']]}
              />
            </Card>
            <Card sx={{ p: 2 }}>
              <ProjectCompact idNumber={idNumber} input={input} setInput={setInput} />
            </Card>
            <Card sx={{ p: 2 }}>
              <BillingTable
                entityId={idNumber}
                idEntityType={ENTITY_TYPE.CLIENT}
                billToDisplay={false}
                disabled={disabled}
                countries={countries}
              />
            </Card>
          </Stack>
        )}
        <div className="d-flex justify-content-end my-3">
          {!idNumber && !disabled ? (
            <>
              {loading === true ? (
                <Box display={'flex'} justifyContent={'flex-end'} gap={1} my={2}>
                  <Spinner small={true} />
                </Box>
              ) : (
                <Box display={'flex'} justifyContent={'flex-end'} gap={1} my={2}>
                  <Button type="button" onClick={handleBackClick}>
                    <ArrowBackIcon />
                  </Button>
                  <PermissionsGate
                    viewScopes={[SCOPES['clients.edit']]}
                    editScopes={[SCOPES['clients.edit']]}
                    editRoles={[ROLES.clientWriter]}
                    viewRoles={[ROLES.clientReader]}
                  >
                    <ButtonWithOptions
                      color="primary"
                      variant="contained"
                      disabled={Object.keys(errors).length > 0 || disabledNew}
                      menu={
                        <>
                          <MenuItem
                            onClick={handleSaveCloseClick}
                            disabled={Object.keys(errors).length > 0 || disabledNew}
                          >
                            {intl.get('save&close.button')}
                          </MenuItem>
                        </>
                      }
                       onClick={handleSaveClick}
                    >
                      <SaveIcon className="mb-1" /> {intl.get('save.button')}
                    </ButtonWithOptions>
                  </PermissionsGate>
                </Box>
              )}
            </>
          ) : null}
          {idNumber && disabled ? (
            <Box display={'flex'} justifyContent={'flex-end'} gap={1}>
              <Button onClick={handleBackClick}>
                <ArrowBack />
              </Button>
              <Button type="button" onClick={handleEditMode} startIcon={<EditIcon />}>
                {intl.get('edit.button')}
              </Button>
            </Box>
          ) : null}
          {idNumber && !disabled ? (
            <Box display={'flex'} justifyContent={'flex-end'} gap={1}>
              <Button type="button" onClick={handleEditMode}>
                {intl.get('cancel.button')}
              </Button>
              <PermissionsGate
                viewScopes={[SCOPES['clients.edit']]}
                editScopes={[SCOPES['clients.edit']]}
                editRoles={[ROLES.clientWriter]}
                viewRoles={[ROLES.clientReader]}
              >
                <ButtonWithOptions
                  color="primary"
                  variant="contained"
                  disabled={Object.keys(errors).length > 0 || disabled}
                  menu={
                    <>
                      <MenuItem
                        onClick={handleSaveCloseClick}
                        disabled={Object.keys(errors).length > 0 || disabled}
                      >
                        {intl.get('save&close.button')}
                      </MenuItem>
                    </>
                  }
                       onClick={handleSaveClick}
                >
                  <SaveIcon className="mb-1" /> {intl.get('save.button')}
                </ButtonWithOptions>
              </PermissionsGate>
            </Box>
          ) : null}
        </div>
      </Box>
    </Box>
  );
};

export default ClientDetail;
